import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AccountService } from '@nano/account/common';
import { ConfirmModalService } from '@nano/common/confirm-modal';
import { NavService } from '@nano/common/nav';
import { ApiEndpoints, ApiService } from '@nano/core/api';
import { AuthService, UserProfile } from '@nano/core/auth';
import { CONFIG } from '@nano/core/config';
import { L10nTranslationService } from 'angular-l10n';
import { Observable, combineLatest, of } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';
@Component({
  selector: 'app-user-menu-items',
  templateUrl: './user-menu-items.component.html',
  styleUrls: ['./user-menu-items.component.less'],
  standalone: false
})
export class UserMenuItemsComponent {
  @Output() itemClicked: EventEmitter<void> = new EventEmitter<void>();
  @Input() mobile = false;
  @Input() menuVisible: boolean;

  hasAccount$: Observable<boolean>;
  displayVerification$: Observable<boolean>;
  userVerified$: Observable<boolean>;
  userProfile$: Observable<UserProfile>;
  businessAccountEnabled: boolean;
  openBusinessAccountEnabled$: Observable<boolean>;
  personalAccountEnabled: boolean;
  openPersonalAccountEnabled$: Observable<boolean>;
  displaySwitchProfile$: Observable<boolean>;
  referralEnabled: boolean;
  helpEnabled: boolean;
  partyId: string;

  private config = inject(CONFIG);

  constructor(
    private router: Router,
    private authService: AuthService,
    private translationService: L10nTranslationService,
    private accountService: AccountService,
    private navService: NavService,
    private confirmModalService: ConfirmModalService,
    private apiService: ApiService
  ) {
    this.hasAccount$ = this.accountService.hasAccount$;
    this.userProfile$ = this.authService.userProfile$;
    this.userVerified$ = this.navService.userVerified$;

    this.displayVerification$ = combineLatest([this.userVerified$, this.navService.verificationInfoVisible$]).pipe(
      map(([userVerified, privatePerson]) => !userVerified && privatePerson && this.config.displayVerificationMenu),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.businessAccountEnabled = this.config.businessAccountEnabled;
    this.personalAccountEnabled = this.config.personalAccountEnabled;
    this.referralEnabled = this.config.referralEnabled;
    this.helpEnabled = this.config.helpEnabled;

    this.openBusinessAccountEnabled$ = this.navService.privatePerson$.pipe(
      map((privatePerson) => privatePerson && this.businessAccountEnabled),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.openPersonalAccountEnabled$ = of(this.personalAccountEnabled && this.businessAccountEnabled && this.authService.isEligibleForPersonalAccount()).pipe(
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.displaySwitchProfile$ = this.authService.userProfiles$.pipe(
      map((userProfiles) => (userProfiles ?? []).length > 1),
      shareReplay({ bufferSize: 1, refCount: false })
    );
  }

  settings(): void {
    this.itemClicked.emit();
    void this.router.navigate(['./settings']);
  }

  profileInformation(): void {
    this.itemClicked.emit();
    void this.router.navigate(['./settings/profile/profile-information']);
  }

  goToVerification(): void {
    this.itemClicked.emit();
    void this.router.navigate(['./settings/profile/verification']);
  }

  contacts(): void {
    this.itemClicked.emit();
  }

  invite(): void {
    this.itemClicked.emit();
    void this.router.navigate(['./settings/profile/referral']);
  }

  help(): void {
    this.itemClicked.emit();
    void this.router.navigate(['./messages/new']);
  }

  switch(): void {
    this.itemClicked.emit();
    this.authService.switchProfile();
  }

  businessAccount(): void {
    this.itemClicked.emit();
    const businessAccountUrl = this.translationService.translate('url.businessAccount', { oauthAuthority: this.config.oauthAuthority }) as string;
    window.open(businessAccountUrl, '_blank');
  }

  openPersonalAccount(): void {
    const title = this.translationService.translate('menu.user.openingPersonalAccountConfirmation') as string;
    this.confirmModalService.openModal(title, () => {
      this.apiService
        .post(ApiEndpoints.contractUser())
        .pipe(take(1))
        .subscribe(() => {
          this.itemClicked.emit();
          this.authService.switchProfile();
        });
    });
  }
  logout(): void {
    this.itemClicked.emit();
    this.authService.logout(undefined, 'logoutButton').pipe(take(1)).subscribe();
  }
}
